import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { DiscountApprovalProvider } from './contexts/DiscountApprovalContext';
import { can, PERMS, menuPagesFor, startPathFor } from './utils/accessControl';
import { useBusinessInfo } from './hooks/useBusinessInfo';
import { useBranchFeatures } from './hooks/useBranchFeatures';
import { useAppStore } from './stores/useAppStore';
import Layout from './components/Layout';
import Login from './components/Login';
import POSInterface from './components/POS/POSInterface';
import ItemManagement from './components/Items/ItemManagement';
import ItemStockHistory from './components/Items/ItemStockHistory';
import SalesList from './components/Sales/SalesList';
import SaleDetail from './components/Sales/SaleDetail';
import SaleReturnList from './components/SaleReturn/SaleReturnList';
import SaleReturnDetail from './components/SaleReturn/SaleReturnDetail';
import SaleReturnEdit from './components/SaleReturn/SaleReturnEdit';
import SaleReturnForm from './components/SaleReturn/SaleReturnForm';
import PurchaseList from './components/Purchases/PurchaseList';
import PurchaseDetail from './components/Purchases/PurchaseDetail';
import PurchaseForm from './components/Purchases/PurchaseForm';
import PurchaseReturnList from './components/PurchaseReturn/PurchaseReturnList';
import PurchaseReturnDetail from './components/PurchaseReturn/PurchaseReturnDetail';
import PurchaseReturnForm from './components/PurchaseReturn/PurchaseReturnForm';
import DamagesPage from './components/Damages/DamagesPage';
import DamageDispatchList from './components/Damages/DamageDispatchList';
import DamageDispatchDetail from './components/Damages/DamageDispatchDetail';
import ExchangeList from './components/Exchange/ExchangeList';
import ExchangeDetail from './components/Exchange/ExchangeDetail';
import ExchangeForm from './components/Exchange/ExchangeForm';
import { ClosingList, ClosingForm, ClosingDetail } from './components/Closing';
import { ExpenseList } from './components/Expenses';
import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';
import Analytics2Dashboard from './components/Analytics2/Analytics2Dashboard';
import { BusinessInformationPage } from './components/BusinessInformation';
import SalesmanScanner from './components/Salesman/SalesmanScanner';
import ReportList from './components/Reports/ReportList';
import ReportBuilder from './components/Reports/ReportBuilder';
import ReportRunner from './components/Reports/ReportRunner';
import ReportEditDispatcher from './components/Reports/ReportEditDispatcher';
import SqlWorkbench from './components/Reports/SqlWorkbench';
import UsersPage from './components/Users/UsersPage';
import RolesPage from './components/Users/RolesPage';
import NotificationContainer from './components/layout/NotificationContainer';
import UpdateNotifier from './components/UpdateNotifier';
import DynamicFavicon from './components/DynamicFavicon';


const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <Spinner />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// A page that only exists on branches that switched its feature on (branches.json `features`).
// Waits for the flags to load rather than treating "still loading" as "off".
const FeatureRoute = ({ feature, children }) => {
  const features = useAppStore((s) => s.features);

  if (features === null) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!features[feature]) {
    return <div className="p-8 text-center text-gray-600">This page is not enabled for this branch.</div>;
  }

  return children;
};

// Shown inside the layout, so the top bar and the Ctrl+M menu stay in reach.
const AccessDenied = ({ page }) => {
  const { user } = useAuth();
  const features = useAppStore((s) => s.features);
  const navigate = useNavigate();
  const start = menuPagesFor(user, features)[0];

  return (
    <div className="flex items-center justify-center py-16">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
        <p className="text-gray-600 mb-1">
          {page ? `Your role (${user?.role?.name || 'none'}) can't open ${page}.` : 'Your role has no pages yet.'}
        </p>
        <p className="text-gray-600 mb-6">Ask an admin if you need it.</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Go Back
          </button>
          {start && (
            <button
              onClick={() => navigate(start.path)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to {start.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// A page for the roles that grant `permission` (a key, or a list that must all be granted).
const Allow = ({ permission, page, children }) => {
  const { user } = useAuth();
  return can(user, permission) ? children : <AccessDenied page={page} />;
};

// `/`: the POS for roles that sell; anyone else starts on their first menu page
// (a Salesman on the Scanner, as before).
const StartPage = () => {
  const { user } = useAuth();
  const features = useAppStore((s) => s.features);

  if (can(user, PERMS.POS)) {
    return (
      <Layout>
        <POSInterface />
      </Layout>
    );
  }
  const start = startPathFor(user, features);
  if (start) return <Navigate to={start} replace />;
  if (features === null) return <Spinner />; // the first page may be one a branch feature adds
  return (
    <Layout>
      <AccessDenied />
    </Layout>
  );
};

// Signed in, inside the layout, and allowed by the role (and the branch, for `feature`).
const page = (permission, label, element, feature) => (
  <ProtectedRoute>
    <Layout>
      <Allow permission={permission} page={label}>
        {feature ? <FeatureRoute feature={feature}>{element}</FeatureRoute> : element}
      </Allow>
    </Layout>
  </ProtectedRoute>
);

const BUILD_REPORTS = [PERMS.REPORTS, PERMS.REPORTS_MANAGE];

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" /> : <Login />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <StartPage />
          </ProtectedRoute>
        }
      />
      <Route path="/scanner" element={page(PERMS.SCANNER, 'the Scanner', <SalesmanScanner />)} />
      <Route path="/items" element={page(PERMS.ITEMS, 'Items', <ItemManagement />)} />
      <Route path="/items/:id/history" element={page(PERMS.ITEMS, 'Items', <ItemStockHistory />)} />
      <Route path="/sales" element={page(PERMS.SALES, 'Sales', <SalesList />)} />
      <Route path="/sales/:id" element={page(PERMS.SALES, 'Sales', <SaleDetail />)} />
      <Route path="/sale-return" element={page(PERMS.SALE_RETURNS, 'Sale Returns', <SaleReturnList />)} />
      <Route path="/sale-return/new" element={page(PERMS.SALE_RETURNS, 'Sale Returns', <SaleReturnForm />)} />
      <Route path="/sale-return/:id" element={page(PERMS.SALE_RETURNS, 'Sale Returns', <SaleReturnDetail />)} />
      <Route path="/purchases" element={page(PERMS.PURCHASES, 'Purchases', <PurchaseList />)} />
      <Route path="/purchases/new" element={page(PERMS.PURCHASES, 'Purchases', <PurchaseForm />)} />
      <Route path="/purchases/:id" element={page(PERMS.PURCHASES, 'Purchases', <PurchaseDetail />)} />
      <Route path="/purchases/:id/edit" element={page(PERMS.PURCHASES, 'Purchases', <PurchaseForm />)} />
      <Route path="/damages" element={page(PERMS.DAMAGES, 'Damages', <DamagesPage />, 'damages')} />
      <Route
        path="/damages/dispatches"
        element={page(PERMS.DAMAGES, 'Damages', <DamageDispatchList />, 'damages')}
      />
      <Route
        path="/damages/dispatches/:id"
        element={page(PERMS.DAMAGES, 'Damages', <DamageDispatchDetail />, 'damages')}
      />
      <Route
        path="/purchase-returns"
        element={page(PERMS.PURCHASE_RETURNS, 'Purchase Returns', <PurchaseReturnList />)}
      />
      <Route
        path="/purchase-returns/new"
        element={page(PERMS.PURCHASE_RETURNS, 'Purchase Returns', <PurchaseReturnForm />)}
      />
      <Route
        path="/purchase-returns/:id"
        element={page(PERMS.PURCHASE_RETURNS, 'Purchase Returns', <PurchaseReturnDetail />)}
      />
      <Route path="/exchange" element={page(PERMS.EXCHANGES, 'Exchanges', <ExchangeList />)} />
      <Route path="/exchange/new" element={page(PERMS.EXCHANGES, 'Exchanges', <ExchangeForm />)} />
      <Route path="/exchange/:id" element={page(PERMS.EXCHANGES, 'Exchanges', <ExchangeDetail />)} />
      <Route path="/exchange/:id/edit" element={page(PERMS.EXCHANGES, 'Exchanges', <ExchangeForm />)} />
      <Route path="/expenses" element={page(PERMS.EXPENSES, 'Expenses', <ExpenseList />)} />
      <Route path="/closings" element={page(PERMS.CLOSINGS, 'Closings', <ClosingList />)} />
      <Route path="/closings/new" element={page(PERMS.CLOSINGS, 'Closings', <ClosingForm />)} />
      <Route path="/closings/:id" element={page(PERMS.CLOSINGS, 'Closings', <ClosingDetail />)} />
      {/* Closing edit route stays off - editing disabled */}
      <Route path="/analytics" element={page(PERMS.ANALYTICS, 'Analytics', <AnalyticsDashboard />)} />
      <Route path="/analytics2" element={page(PERMS.ANALYTICS, 'Analytics', <Analytics2Dashboard />)} />
      <Route path="/reports" element={page(PERMS.REPORTS, 'Reports', <ReportList />)} />
      <Route path="/reports/new" element={page(BUILD_REPORTS, 'the report builder', <ReportBuilder />)} />
      <Route path="/reports/sql" element={page(BUILD_REPORTS, 'the report builder', <SqlWorkbench />)} />
      <Route path="/reports/:id" element={page(PERMS.REPORTS, 'Reports', <ReportRunner />)} />
      <Route
        path="/reports/:id/edit"
        element={page(BUILD_REPORTS, 'the report builder', <ReportEditDispatcher />)}
      />
      <Route
        path="/business-information"
        element={page(PERMS.BUSINESS_INFO, 'Business Information', <BusinessInformationPage />)}
      />
      <Route path="/users" element={page(PERMS.USERS, 'Users', <UsersPage />)} />
      <Route path="/users/roles" element={page(PERMS.USERS, 'Roles', <RolesPage />)} />
    </Routes>
  );
};

// Component to load business information on app startup
const BusinessInfoLoader = ({ children }) => {
  useBusinessInfo(); // This will load business info and cache it
  useBranchFeatures(); // which optional features (e.g. damages) this branch has
  return children;
};

function App() {

  return (
    <AuthProvider>
      <CurrencyProvider>
        <CartProvider>
          <NavigationProvider>
            <DiscountApprovalProvider>
              <BusinessInfoLoader>
                <Router>
                  <div className="App">
                    <DynamicFavicon />
                    <AppRoutes />
                    <NotificationContainer />
                    <UpdateNotifier />
                  </div>
                </Router>
              </BusinessInfoLoader>
            </DiscountApprovalProvider>
          </NavigationProvider>
        </CartProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
